import React, { useRef } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { FileSignature, Upload } from 'lucide-react';

/**
 * Handles both Edit Tenant Details (mode='edit') and New Tenancy (mode='new')
 * via the same form. Parent owns formData + handlers; this component is
 * purely presentational + drives the hidden file input for AST upload.
 */
export default function EditTenantDialog({
  open,
  onOpenChange,
  newTenancyMode,
  formData,
  setFormData,
  astFile,
  aiLoading,
  onTenancyUpload,
  onSubmit,
}) {
  const fileInputRef = useRef(null);
  const saveLabel = newTenancyMode ? 'Save New Tenancy' : 'Save Tenant Details';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="edit-property-dialog">
        <DialogHeader>
          <DialogTitle>{newTenancyMode ? 'New Tenancy' : 'Edit Tenant Details'}</DialogTitle>
          <DialogDescription>
            {newTenancyMode
              ? 'Start a new tenancy. The current tenant snapshot will be archived, and uploading a new AST replaces the existing Tenancy Agreement document.'
              : 'Update tenant details for the current tenancy. Upload a fresh tenancy agreement to auto-fill the fields with AI.'}
          </DialogDescription>
          {!newTenancyMode && (
            <p className="dialog-subtitle">For corrections and manual updates. To start a brand-new tenancy, close this and use the New Tenancy button.</p>
          )}
        </DialogHeader>
        <form onSubmit={onSubmit} className="property-form">
          <div className="tenant-ai-extract">
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              ref={fileInputRef}
              onChange={onTenancyUpload}
              style={{ display: 'none' }}
              data-testid="tenancy-upload-input"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn-tenant-ai"
              disabled={aiLoading}
              data-testid="tenant-ai-extract-button"
            >
              {aiLoading ? (
                <>
                  <span className="ai-spinner" />
                  Uploading & reading with AI…
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload Tenancy Agreement (AI auto-fill)
                </>
              )}
            </Button>
            <div className="tenant-ai-hint">
              PDF is attached to Documents instantly. AI fills the fields below.
            </div>
            {astFile && (
              <div className="tenant-ast-attached" data-testid="tenant-ast-attached">
                <FileSignature size={14} />
                <span>
                  <strong>{astFile.name}</strong> attached to Documents as the Tenancy Agreement.
                </span>
              </div>
            )}
          </div>

          <div className="form-group">
            <Label htmlFor="edit-tenant-name">Tenant Name</Label>
            <Input
              id="edit-tenant-name"
              value={formData.tenant_name || ''}
              onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
              required
              data-testid="edit-tenant-name-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="edit-tenant-email">Tenant Email <span className="form-hint-inline">(for renewal reminders)</span></Label>
            <Input
              id="edit-tenant-email"
              type="email"
              placeholder="tenant@example.com"
              value={formData.tenant_email || ''}
              onChange={(e) => setFormData({ ...formData, tenant_email: e.target.value })}
              data-testid="edit-tenant-email-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="edit-tenant-phone">Tenant Phone <span className="form-hint-inline">(optional)</span></Label>
            <Input
              id="edit-tenant-phone"
              type="tel"
              placeholder="07123 456789"
              value={formData.tenant_phone || ''}
              onChange={(e) => setFormData({ ...formData, tenant_phone: e.target.value })}
              data-testid="edit-tenant-phone-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="edit-tenant-rent">Monthly Rent (£)</Label>
            <Input
              id="edit-tenant-rent"
              type="number"
              step="0.01"
              value={formData.rent_amount ?? ''}
              onChange={(e) => setFormData({ ...formData, rent_amount: e.target.value })}
              required
              data-testid="edit-tenant-rent-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="edit-tenant-due">Rent Due Date</Label>
            <Select
              value={String(formData.rent_due_date || 1)}
              onValueChange={(value) => setFormData({ ...formData, rent_due_date: parseInt(value, 10) })}
            >
              <SelectTrigger data-testid="edit-tenant-due-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[...Array(31)].map((_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {i + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="form-group">
            <Label htmlFor="edit-tenant-start">Tenancy Start Date</Label>
            <Input
              id="edit-tenant-start"
              type="date"
              value={formData.tenancy_start_date || ''}
              onChange={(e) => setFormData({ ...formData, tenancy_start_date: e.target.value })}
              required
              data-testid="edit-tenant-start-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="edit-tenant-end">Tenancy End Date</Label>
            <Input
              id="edit-tenant-end"
              type="date"
              value={formData.tenancy_end_date || ''}
              onChange={(e) => setFormData({ ...formData, tenancy_end_date: e.target.value })}
              required
              data-testid="edit-tenant-end-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="edit-tenant-contract">Contract Type</Label>
            <Select
              value={formData.contract_type || 'AST'}
              onValueChange={(value) => setFormData({ ...formData, contract_type: value })}
            >
              <SelectTrigger data-testid="edit-tenant-contract-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AST">AST</SelectItem>
                <SelectItem value="Licence">Licence</SelectItem>
                <SelectItem value="Company Let">Company Let</SelectItem>
                <SelectItem value="Periodic">Periodic</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="form-group">
            <Label htmlFor="edit-tenant-deposit">Deposit (£)</Label>
            <Input
              id="edit-tenant-deposit"
              type="number"
              step="0.01"
              value={formData.deposit_amount ?? ''}
              onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })}
              placeholder="Optional"
              data-testid="edit-tenant-deposit-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="edit-tenant-cv">Contract Value (£)</Label>
            <Input
              id="edit-tenant-cv"
              type="number"
              step="0.01"
              value={formData.contract_value ?? ''}
              onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
              placeholder="Optional"
              data-testid="edit-tenant-cv-input"
            />
          </div>
          <Button type="submit" className="btn-primary w-full" data-testid="edit-tenant-save">
            {saveLabel}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
